import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, AlertCircle, Loader2, CheckCircle2 } from 'lucide-react';
import clsx from 'clsx';
import { useAuthStore } from '../../stores/authStore';
import { AuthImagePanel } from '../../components/AuthImagePanel/AuthImagePanel';

/**
 * Forgot password page — sends Supabase magic link for password reset.
 */
export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const { resetPassword, loading, error, clearError } = useAuthStore();

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();
    const res = await resetPassword(email);
    if (res.success) setSent(true);
  };

  return (
    <div className="min-h-screen flex bg-ui-background">
      <AuthImagePanel />

      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <div className="mb-8">
            <Link to="/" className="text-3xl font-extrabold text-brand-dark italic tracking-tight">
              smothers
            </Link>
            <h1 className="text-2xl font-bold text-brand-dark mt-4">Reset your password</h1>
            <p className="text-sm text-brand-gray mt-1">
              Enter your email and we'll send you a reset link.
            </p>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-start gap-3 p-3 mb-5 bg-red-50 border border-error/20 rounded-xl"
              role="alert"
              aria-live="assertive"
            >
              <AlertCircle size={18} className="text-error flex-shrink-0 mt-0.5" />
              <p className="text-sm text-error">{error}</p>
            </motion.div>
          )}

          {sent ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-8"
            >
              <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 size={32} className="text-success" />
              </div>
              <h2 className="text-lg font-bold text-brand-dark mb-2">Check your email</h2>
              <p className="text-sm text-brand-gray mb-6 max-w-sm mx-auto">
                We sent a password reset link to <strong className="text-brand-dark">{email}</strong>.
                Click the link to set a new password.
              </p>
              <Link
                to="/admin/login"
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-brand-red hover:bg-red-700 text-brand-white text-sm font-bold rounded-xl shadow-lg transition-colors"
              >
                Back to Sign In
              </Link>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-brand-dark mb-1.5">
                  Email address
                </label>
                <div className="relative">
                  <Mail size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ui-muted" />
                  <input
                    id="email"
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@smothers.com"
                    className="w-full pl-11 pr-4 py-3 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/30 focus:border-brand-red transition-all placeholder:text-ui-muted"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className={clsx(
                  'w-full flex items-center justify-center gap-2 py-3 text-sm font-bold rounded-xl transition-all',
                  loading
                    ? 'bg-brand-red/60 text-brand-white cursor-not-allowed'
                    : 'bg-brand-red hover:bg-red-700 text-brand-white shadow-lg hover:shadow-xl'
                )}
              >
                {loading && <Loader2 size={18} className="animate-spin" />}
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>

              <p className="text-center text-sm text-brand-gray">
                Remember your password?{' '}
                <Link to="/admin/login" className="text-brand-red hover:text-red-700 font-medium">
                  Sign in
                </Link>
              </p>
            </form>
          )}

          <p className="mt-8 text-center text-xs text-brand-gray">
            <Link to="/" className="text-brand-red hover:text-red-700 font-medium">
              &larr; Back to website
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
